'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from '@/i18n/routing';
import { useLocale } from 'next-intl';
import { useAuthStore } from '@/stores/auth';
import { useNotificationsStore } from '@/stores/notifications';
import { useFCM } from '@/hooks/useFCM';
import { FCM_ANONYMOUS_ID_KEY, claimGuestTokens, getStoredAnonymousId } from '@/lib/fcm-utils';

export function AuthInitializer() {
  const locale = useLocale();
  const authStore = useAuthStore();
  const notificationsStore = useNotificationsStore();
  const prevUserRef = useRef(authStore.user);
  const pathname = usePathname();

  // Initialize FCM when user is logged in
  useFCM();

  useEffect(() => {
    // Skip auth check if we're on the callback page - let it handle its own auth
    if (pathname?.includes('/auth/callback')) {
      return;
    }

    // Always fetch user data on mount to check authentication status (with ?lang= for translated labels)
    authStore.fetchUser({ lang: locale }).catch(() => {
      // Silently fail if not authenticated - this is expected for unauthenticated users
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, locale]);

  // Monitor user state changes: logout cleanup and claim guest FCM tokens on login
  useEffect(() => {
    const currentUser = authStore.user;
    const prevUser = prevUserRef.current;

    if (prevUser !== null && currentUser === null) {
      // Logout: clear notifications store and related localStorage
      notificationsStore.clearStore();
      if (typeof window !== 'undefined') {
        try {
          localStorage.removeItem('notifications_unread_count');
          localStorage.removeItem('notifications_pending_count');
          localStorage.removeItem('notifications_last_fetched');
        } catch (error) {
          console.error('[AuthInitializer] Error clearing LocalStorage:', error);
        }
      }
    }

    if (prevUser === null && currentUser !== null && typeof window !== 'undefined') {
      // Login: claim guest FCM tokens so anonymous device receives user-specific push
      const anonymousId = getStoredAnonymousId();
      if (anonymousId) {
        claimGuestTokens(anonymousId, authStore.authorizedFetch).finally(() => {
          try {
            localStorage.removeItem(FCM_ANONYMOUS_ID_KEY);
          } catch {
            // ignore
          }
        });
      }
    }

    prevUserRef.current = currentUser;
  }, [authStore.user, authStore.authorizedFetch, notificationsStore]);

  return null;
}

